import { useContext, useEffect } from 'react';
import { Col, Modal } from 'react-bootstrap';
import { useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import Collapse from 'components/collapse/Collapse';
import DailyRateField from 'components/forms/dailyRateField/DailyRateField';
import DateTimePickerField from 'components/forms/dateTimePickerField/DateTimePickerField';
import FormFieldset from 'components/forms/form/FormFieldset';
import TextField from 'components/forms/textField/TextField';
import ModalFormCancelButton from 'components/modal/form/ModalFormCancelButton';
import ModalFormSubmitButton from 'components/modal/form/ModalFormSubmitButton';
import useCalendarBodySelection from 'pages/calendar/common/body/selection/useCalendarBodySelection';
import {
  PropertyPricingModalFooter,
  PropertyPricingWrapper,
} from './PropertyPricingModal.styles';
import { PropertiesPricingData } from './PropertyPricingModal.types';
import PropertyPricingModalContext from './PropertyPricingModalContext';
import PropertyPricingModalRestrictions from './PropertyPricingModalRestrictions';
import useResetPropertiesPricing from './useResetPropertiesPricing';

const PropertyPricingModalBodyInner = ({
  propertiesPricingData,
}: {
  propertiesPricingData: PropertiesPricingData;
}) => {
  const { t } = useTranslation();

  const { updateSelectionDates, resetSelection } = useCalendarBodySelection();
  const { setSelectionDates } = useContext(PropertyPricingModalContext);

  const cancelHandler = () => {
    resetSelection();
  };
  const { isResetting, resetPropertiesPricing } = useResetPropertiesPricing();
  const { isSubmitting, errors } = useFormState();
  const { startDate, endDate } = useWatch();

  useEffect(() => {
    updateSelectionDates(startDate, endDate);
    setSelectionDates({ start: startDate, end: endDate });
  }, [startDate, endDate, setSelectionDates]);

  return (
    <>
      <Modal.Body>
        <PropertyPricingWrapper>
          <FormFieldset disabled={isResetting}>
            <DateTimePickerField
              name="startDate"
              label={t('componentProperty.modal.form.startDate')}
              colSmInput={5}
              colSmOffsetInput={3}
              colSmLabel={4}
            />

            <DateTimePickerField
              name="endDate"
              label={t('componentProperty.modal.form.endDate')}
              colSmInput={5}
              colSmOffsetInput={3}
              colSmLabel={4}
            />

            <Collapse
              defaultExpanded
              label={t('componentProperty.modal.pricing')}
            >
              {Object.keys(propertiesPricingData.pricing).map((key, index) => {
                return (
                  <DailyRateField
                    key={key}
                    id={`${index}`}
                    currency={key}
                    name={`pricing.${index}`}
                    placeholder={
                      propertiesPricingData.pricing[key].minDailyRate ===
                      propertiesPricingData.pricing[key].maxDailyRate
                        ? `${propertiesPricingData.pricing[key].minDailyRate}`
                        : `${propertiesPricingData.pricing[key].minDailyRate} - ${propertiesPricingData.pricing[key].maxDailyRate}`
                    }
                  />
                );
              })}

              <TextField
                label={t('componentProperty.modal.form.minimumStay')}
                name="minimumStay"
                type="number"
                min={0}
                colSmInput={5}
                colSmOffsetInput={3}
                colSmLabel={4}
                placeholder={
                  propertiesPricingData.minMinimumStay ===
                  propertiesPricingData.maxMinimumStay
                    ? `${propertiesPricingData.minMinimumStay}`
                    : `${propertiesPricingData.minMinimumStay} - ${propertiesPricingData.maxMinimumStay}`
                }
                rightAddons={[t('componentProperty.modal.form.nights')]}
              />
            </Collapse>

            <Collapse
              data-testid="check-in-checkout-restrictions"
              defaultExpanded={false}
              label={t('componentProperty.modal.checkInCheckoutRestrictions')}
            >
              <PropertyPricingModalRestrictions />
            </Collapse>
          </FormFieldset>
        </PropertyPricingWrapper>
      </Modal.Body>

      <Modal.Footer data-testid="pricing-modal-footer">
        <Col sm={12}>
          {errors.invalid && (
            <p className="text-danger text-center">
              {errors.invalid.message?.toString()}
            </p>
          )}
        </Col>
        <PropertyPricingModalFooter sm={12}>
          <Button
            bsStyle="danger"
            data-testid="pricing-modal-reset-button"
            disabled={
              isSubmitting || isResetting || Object.keys(errors).length > 0
            }
            onClick={resetPropertiesPricing}
          >
            {t('common.reset')}
          </Button>

          <ModalFormCancelButton
            data-testid="pricing-modal-cancel-button"
            onClick={cancelHandler}
          />
          <ModalFormSubmitButton
            data-testid="pricing-modal-save-button"
            disabled={isResetting}
            disabledIfNotDirty={false}
          />
        </PropertyPricingModalFooter>
      </Modal.Footer>
    </>
  );
};

export default PropertyPricingModalBodyInner;
